/* eslint-disable camelcase */
import AppContext from "PFApp/app_context";
import ConfirmModal from "PFComponents/confirm/confirm_modal";
import PageLayout from "PFComponents/page/page_layout";
import Cover from "PFCore/components/cover";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import { FeatureFlag } from "PFTypes";
import PropTypes from "prop-types";
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { Prompt } from "react-router-dom";

import css from "./activity_edit_form.less";
import { CancelButton } from "./components/cancel_button";
import { SetSkillsFrameworkButton } from "./components/set_skills_framework_button";
import { ActivityEditFormSidePanel } from "./sections/activity_edit_form_side_panel";
import { AttributesSection } from "./sections/attributes_section";
import AvailabilitySection from "./sections/availability_section";
import BasicSection from "./sections/basic_section";
import ButtonSection from "./sections/button_section";
import EngagementWizardButtonSection from "./sections/engagement_wizard_button_section";
import HeaderSection from "./sections/header_section";
import buttonCss from "./sections/header_section.module.scss";
import { useActivityEditForm } from "./use_activity_edit_form";
import { ModalKey } from "./utils/use_confirm_modals_manager";

const ActivityEditForm = ({
  template,
  subtemplates,
  activity,
  parentActivity,
  activityAttachments,
  shortlistsToCopy,
  isClone
}) => {
  const { t } = useTranslation("activities");
  const isEnabled = useIsFeatureEnabled();
  const {
    store: { templatesConfiguration }
  } = useContext(AppContext);

  const isNew = !activity.id;
  const isEdit = !isNew;
  const isDraft = activity.state === "draft";
  const isEngagement = ["engagement", "audit_engagement"].includes(template.key);
  const isRole = ["role", "audit_role"].includes(template.key);
  const isForceVisibilitySet = !!template.force_visibility;
  const isForcePrivate = template.force_visibility === "private";

  const qaIdPrefix = `activity-${activity.id ? "edit" : "create"}`;
  const templateConfig = templatesConfiguration.find(({ template_key }) => template_key === template.key);

  const requireAssignee = isEnabled(FeatureFlag.RequireAssignee) && (isEngagement || isRole);

  const {
    name,
    setName,
    description,
    setDescription,
    customFields,
    setCustomFields,
    postUntil,
    skillsFramework,
    setSkillsFramework,
    selectedParentActivity,
    setSelectedParentActivity,
    assignee,
    setAssignee,
    attachments,
    setAttachments,
    metadata,
    setMetadata,
    postUntilChangeReason,
    setPostUntilChangeReason,
    setSubmitOptions,
    minRequiredCount,
    setMinRequiredCount,
    isPrivate,
    setIsPrivate,
    audiences,
    setAudiences,
    communicationType,
    setCommunicationType,
    questionsToggleValue,
    handleUploadRemove,
    handleSubmit,
    questionsRef,
    showQuestionsTile,
    subtemplateOptions,
    hasSubtemplates,
    subtemplate,
    subtemplateKey,
    isLoading,
    handleSubtemplateChange,
    errors,
    setErrors,
    renderGrowlError,
    locked,
    isModalDisplayed,
    setModalDisplayed,
    setModalConfirmed,
    engagementWizardState,
    handleEngagementWizardNavigation,
    handleQuestionChange,
    handleQuestionsToggleChange,
    handlePrivateChange,
    handlePostUntilChange,
    isChanged,
    showSkillsFrameworkButton,
    bookingCategory,
    setBookingCategory
  } = useActivityEditForm({
    template,
    activity,
    parentActivity,
    activityAttachments,
    isClone,
    isEdit,
    isForcePrivate,
    isForceVisibilitySet,
    templateConfig,
    subtemplates,
    requireAssignee,
    shortlistsToCopy
  });

  const header = () => <HeaderSection template={template} activityId={activity.id} />;

  const cancelButton = () => (
    <CancelButton
      activity={activity}
      handleEngagementWizardNavigation={handleEngagementWizardNavigation}
      engagementWizardState={engagementWizardState}
      isRole={isRole}
    />
  );

  const buttons = ({ isOneColumn }) =>
    isRole && engagementWizardState ? (
      <EngagementWizardButtonSection
        onSubmit={handleSubmit}
        isDraft={isDraft}
        activityId={activity.id}
        wizardState={engagementWizardState}
        isOneColumn={isOneColumn}
      />
    ) : (
      <ButtonSection onSubmit={handleSubmit} isDraft={isDraft} activityId={activity.id} />
    );

  const main = (
    <div className={css.formWrapper}>
      <div style={{ position: "relative" }}>
        <div className="row">
          <div>
            <BasicSection
              name={name}
              customFields={customFields}
              description={description}
              onDescriptionChange={setDescription}
              isRole={isRole}
              isEngagementWizard={isRole && engagementWizardState}
              onNameChange={setName}
              errors={errors}
              parentActivity={selectedParentActivity ?? parentActivity}
              onParentActivityChange={setSelectedParentActivity}
              minRequiredCount={minRequiredCount}
              onMinRequiredCountChange={setMinRequiredCount}
              qaIdPrefix={qaIdPrefix}
              template={template}
            />
            <AttributesSection
              title={t("edit.matchAttributes")}
              buttons={
                showSkillsFrameworkButton && (
                  <SetSkillsFrameworkButton
                    skillsFramework={skillsFramework}
                    onChange={(skillFramework) => setSkillsFramework(skillFramework)}
                    disabled={hasSubtemplates && !subtemplate}
                  />
                )
              }
              name={name}
              description={description}
              template={template}
              metadata={metadata}
              customFields={customFields}
              usedOn={isNew ? "Activity_creation" : "Activity_edition"}
              qaIdPrefix={qaIdPrefix}
              setMetadata={setMetadata}
              setCustomFields={setCustomFields}
              errors={errors}
              isEdit={isEdit}
              isClone={isClone}
              withMatch
            />
            <AvailabilitySection
              template={template}
              metadata={metadata}
              qaIdPrefix={qaIdPrefix}
              onMetadataChange={setMetadata}
              onAvailabilityTypeChange={() => setErrors({})}
              errors={errors}
              activityId={activity?.id}
            />
            <AttributesSection
              title={t("edit.attributes")}
              template={template}
              metadata={metadata}
              customFields={customFields}
              qaIdPrefix={qaIdPrefix}
              setMetadata={setMetadata}
              setCustomFields={setCustomFields}
              errors={errors}
              subtemplateOptions={subtemplateOptions}
              hasSubtemplates={hasSubtemplates}
              subtemplateKey={subtemplateKey}
              onSubtemplateChange={handleSubtemplateChange}
              subtemplate={subtemplate}
              isEdit={isEdit}
              isClone={isClone}
              isLoading={isLoading}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const sidePanel = () => (
    <ActivityEditFormSidePanel
      activity={activity}
      template={template}
      templateConfig={templateConfig}
      isEngagement={isEngagement}
      isRole={isRole}
      audiences={audiences}
      setAudiences={setAudiences}
      postUntil={postUntil}
      handlePostUntilChange={handlePostUntilChange}
      assignee={assignee}
      setAssignee={setAssignee}
      attachments={attachments}
      setAttachments={setAttachments}
      isPrivate={isPrivate}
      setIsPrivate={setIsPrivate}
      communicationType={communicationType}
      setCommunicationType={setCommunicationType}
      questionsToggleValue={questionsToggleValue}
      handleUploadRemove={handleUploadRemove}
      questionsRef={questionsRef}
      showQuestionsTile={showQuestionsTile}
      renderGrowlError={renderGrowlError}
      handleQuestionChange={handleQuestionChange}
      handleQuestionsToggleChange={handleQuestionsToggleChange}
      handlePrivateChange={handlePrivateChange}
      bookingCategory={bookingCategory}
      setBookingCategory={setBookingCategory}
      errors={errors}
      qaIdPrefix={qaIdPrefix}
      requireAssignee={requireAssignee}
      postUntilChangeReason={postUntilChangeReason}
      onPostUntilReasonChange={setPostUntilChangeReason}
    />
  );

  return (
    <div style={{ margin: "auto", maxWidth: 1230 }}>
      <Prompt when={!locked && isChanged} message={t("edit.leaveWithoutSaving")} />
      <PageLayout mode="withSidebar">
        <div>
          <PageLayout.WhenTwoColumns>
            <div style={{ display: "flex", alignItems: "center" }}>
              {locked && <Cover />}
              {header()}
              {cancelButton()}
            </div>
          </PageLayout.WhenTwoColumns>
          <PageLayout.WhenOneColumn>
            {locked && <Cover />}
            {header()}
          </PageLayout.WhenOneColumn>
        </div>
        <div>
          <PageLayout.WhenTwoColumns>{buttons({ isOneColumn: false })}</PageLayout.WhenTwoColumns>
        </div>
      </PageLayout>
      <PageLayout mode="withSidebar">
        <div>
          <PageLayout.WhenTwoColumns>{main}</PageLayout.WhenTwoColumns>

          <PageLayout.WhenOneColumn>
            {main}
            {sidePanel()}
            <div className={buttonCss.alignment}>
              {cancelButton()}
              {buttons({ isOneColumn: true })}
            </div>
          </PageLayout.WhenOneColumn>
        </div>
        <div>
          <PageLayout.WhenTwoColumns>{sidePanel()}</PageLayout.WhenTwoColumns>
        </div>
      </PageLayout>

      {isModalDisplayed[ModalKey.NoRequirements] && (
        <ConfirmModal
          handleOK={() => {
            setModalConfirmed(ModalKey.NoRequirements, true);
          }}
          handleClose={() => {
            setModalDisplayed(ModalKey.NoRequirements, false);
            setSubmitOptions({});
          }}
        >
          {t("edit.creatingNoRequirementsWarning", { templateName: template.name })}
        </ConfirmModal>
      )}
      {isModalDisplayed[ModalKey.Public] && (
        <ConfirmModal
          handleOK={() => {
            setIsPrivate(false);
            setAudiences([]);
            setModalConfirmed(ModalKey.Public, true);
            setModalDisplayed(ModalKey.Public, false);
          }}
          handleClose={() => {
            setModalDisplayed(ModalKey.Public, false);
          }}
        >
          {t("edit.makingRolePublicWarning")}
        </ConfirmModal>
      )}
      {isModalDisplayed[ModalKey.QuestionsChanged] && (
        <ConfirmModal
          handleOK={() => {
            setModalConfirmed(ModalKey.QuestionsChanged, true);
          }}
          handleClose={() => {
            setModalDisplayed(ModalKey.QuestionsChanged, false);
            setModalConfirmed(ModalKey.NoRequirements, false);
            setSubmitOptions({});
          }}
        >
          {t("edit.changedQuestionsWarning")}
        </ConfirmModal>
      )}
      {isModalDisplayed[ModalKey.Misalignment] && (
        <ConfirmModal
          handleOK={() => {
            setModalConfirmed(ModalKey.Misalignment, true);
          }}
          handleClose={() => {
            setModalDisplayed(ModalKey.Misalignment, false);
            setModalConfirmed(ModalKey.NoRequirements, false);
            setModalConfirmed(ModalKey.QuestionsChanged, false);
            setSubmitOptions({});
          }}
        >
          {t("edit.potentialMisalignmentWarning")}
        </ConfirmModal>
      )}
    </div>
  );
};

export default ActivityEditForm;

ActivityEditForm.propTypes = {
  template: PropTypes.object.isRequired,
  subtemplates: PropTypes.array.isRequired,
  activity: PropTypes.object.isRequired,
  parentActivity: PropTypes.object,
  activityAttachments: PropTypes.array.isRequired,
  shortlistsToCopy: PropTypes.arrayOf(PropTypes.shape({ profile: PropTypes.object })),
  isClone: PropTypes.bool
};
